import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, qs } from './api';
import type { ActivityItem, AgentStatus, Cashflow, Customer, CustomerList, Dashboard, Doc, DocDetail, Ingestion, Paged, Settings } from './types';

export const keys = {
  customers: (p: object) => ['customers', p] as const,
  customer: (id: number) => ['customer', id] as const,
  customerActivity: (id: number) => ['customer', id, 'activity'] as const,
  customerOptions: ['customer-options'] as const,
  documents: (p: object) => ['documents', p] as const,
  document: (id: number) => ['document', id] as const,
  ingestions: (p: object) => ['ingestions', p] as const,
  ingestion: (id: number) => ['ingestion', id] as const,
  agent: ['agent-status'] as const,
  dashboard: ['dashboard'] as const,
  cashflow: ['cashflow'] as const,
  settings: ['settings'] as const,
};

export type CustomerListParams = { q?: string; status?: string; paymentTerms?: string; balance?: string; kind?: string; sort: string; dir: string; page: number; pageSize: number };
export const useCustomers = (p: CustomerListParams) =>
  useQuery({ queryKey: keys.customers(p), queryFn: ({ signal }) => api<CustomerList>(`/api/customers${qs(p)}`, { signal }), placeholderData: keepPreviousData });

export const useCustomer = (id: number) => useQuery({ queryKey: keys.customer(id), queryFn: () => api<Customer>(`/api/customers/${id}`), enabled: id > 0 });

export const useCustomerActivity = (id: number) =>
  useQuery({ queryKey: keys.customerActivity(id), queryFn: () => api<{ items: ActivityItem[] }>(`/api/customers/${id}/activity?limit=200`) });

export type CustomerOption = { id: number; businessName: string; companyNumber: string | null; status: string; parentId: number | null };
export const useCustomerOptions = () =>
  useQuery({ queryKey: keys.customerOptions, queryFn: () => api<{ items: CustomerOption[] }>('/api/customers/options'), staleTime: 30_000 });

export type DocumentListParams = {
  customerId?: number;
  includeBranches?: boolean;
  types?: string;
  q?: string;
  from?: string;
  to?: string;
  payment?: string;
  unbilled?: boolean;
  sort?: string;
  dir?: string;
  page?: number;
  pageSize?: number;
};
export const useDocuments = (p: DocumentListParams, enabled = true) =>
  useQuery({
    queryKey: keys.documents(p),
    queryFn: ({ signal }) => api<Paged<Doc> & { sumTotal: number | null }>(`/api/documents${qs(p)}`, { signal }),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useDocument = (id: number | null) =>
  useQuery({ queryKey: keys.document(id ?? 0), queryFn: () => api<DocDetail>(`/api/documents/${id}`), enabled: Boolean(id) });

export const useIngestions = (p: { status?: string; includeIgnored?: boolean; page?: number; pageSize?: number }) =>
  useQuery({
    queryKey: keys.ingestions(p),
    queryFn: () => api<Paged<Ingestion> & { counts: Record<string, number> }>(`/api/ingestions${qs(p)}`),
    placeholderData: keepPreviousData,
  });

export const useIngestion = (id: number | null) =>
  useQuery({ queryKey: keys.ingestion(id ?? 0), queryFn: () => api<Ingestion>(`/api/ingestions/${id}`), enabled: Boolean(id) });

/** Polled: the agent status is how the user knows the integration works. */
export const useAgentStatus = (fast = false) =>
  useQuery({ queryKey: keys.agent, queryFn: () => api<AgentStatus>('/api/agent/status'), refetchInterval: fast ? 3000 : 30_000 });

export const useDashboard = () => useQuery({ queryKey: keys.dashboard, queryFn: () => api<Dashboard>('/api/dashboard'), refetchInterval: 60_000 });
export const useCashflow = () => useQuery({ queryKey: keys.cashflow, queryFn: () => api<Cashflow>('/api/finance/cashflow') });
export const useSettings = () => useQuery({ queryKey: keys.settings, queryFn: () => api<{ settings: Settings; defaults: Settings }>('/api/settings') });
